import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import api from '../utils/api';

const StatCard = ({ icon, label, value, color, to }) => (
  <Link to={to} style={{ textDecoration: 'none' }}>
    <div className="card" style={{ cursor: 'pointer', transition: 'all var(--transition-base)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 'var(--radius-md)',
          background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: color, flexShrink: 0
        }}>
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
          </svg>
        </div>
        <div>
          <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>{value}</div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: 4 }}>{label}</div>
        </div>
      </div>
    </div>
  </Link>
);

const DashboardPage = () => {
  const { user, updateUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramToken, setTelegramToken] = useState(null);
  const telegramPollRef = useRef(null);

  const startTelegramPoll = () => {
    let attempts = 0;
    telegramPollRef.current = setInterval(async () => {
      attempts++;
      try {
        const { data } = await api.get('/auth/profile');
        if (data.user?.telegram_chat_id) {
          updateUser(data.user);
          setTelegramToken(null);
          toast.success('Akun Telegram berhasil terhubung!');
          clearInterval(telegramPollRef.current);
          telegramPollRef.current = null;
        }
      } catch (_) {
        clearInterval(telegramPollRef.current);
        telegramPollRef.current = null;
      }
      if (attempts >= 60) {
        clearInterval(telegramPollRef.current);
        telegramPollRef.current = null;
      }
    }, 2000);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (telegramPollRef.current) {
        clearInterval(telegramPollRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (user?.role === 'admin') {
          const { data } = await api.get('/admin/dashboard');
          setStats(data.stats);
        } else {
          const [regRes, consultRes, learnRes] = await Promise.allSettled([
            api.get('/regulations/stats'),
            api.get('/consultations/stats'),
            api.get('/learning/stats')
          ]);
          setStats({
            totalRegulations: regRes.status === 'fulfilled' ? regRes.value.data.total : 0,
            totalConsultations: consultRes.status === 'fulfilled' ? consultRes.value.data.total : 0,
            totalCourses: learnRes.status === 'fulfilled' ? learnRes.value.data.totalCourses : 0,
            pendingConsultations: consultRes.status === 'fulfilled' ? consultRes.value.data.baru : 0
          });
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
        setStats({ totalRegulations: 0, totalConsultations: 0, totalCourses: 0, pendingConsultations: 0 });
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user]);

  if (loading) {
    return <div className="loading-screen"><div className="spinner" /><span>Memuat dashboard...</span></div>;
  }

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 11) return 'Selamat Pagi';
    if (h < 15) return 'Selamat Siang';
    if (h < 18) return 'Selamat Sore';
    return 'Selamat Malam';
  };

  const handleLinkTelegram = async () => {
    try {
      setTelegramLoading(true);
      const { data } = await api.post('/auth/telegram-token');
      setTelegramToken(data.token);
      window.open(data.link, '_blank');
      startTelegramPoll();
      toast.success('Klik link di Telegram untuk menghubungkan akun');
    } catch (err) {
      console.error('Failed to get Telegram link:', err);
      toast.error('Gagal mendapatkan link Telegram');
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleResetTelegram = async () => {
    if (!window.confirm('Putuskan koneksi Telegram? Anda bisa menghubungkan kembali nanti.')) return;
    try {
      await api.post('/auth/telegram-reset');
      toast.success('Koneksi Telegram diputuskan');
      const { data } = await api.get('/auth/profile');
      updateUser(data.user);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Gagal mereset koneksi');
    }
  };

  const handleReconnectTelegram = async () => {
    try {
      setTelegramLoading(true);
      const { data } = await api.post('/auth/telegram-token');
      setTelegramToken(data.token);
      window.open(data.link, '_blank');
      startTelegramPoll();
      toast.success('Klik link Telegram yang terbuka untuk menghubungkan ulang');
    } catch (err) {
      toast.error('Gagal menyiapkan link ulang Telegram');
    } finally {
      setTelegramLoading(false);
    }
  };

  const copyTelegramToken = async () => {
    if (!telegramToken) return;
    try {
      await navigator.clipboard.writeText(telegramToken);
      toast.success('Token disalin. Kirim ke chat bot Telegram');
    } catch (_) {
      toast.error('Gagal menyalin token');
    }
  };

  return (
    <div className="fade-in">
      {/* Welcome */}
      <div style={{
        background: 'linear-gradient(135deg, var(--orange-600), var(--orange-800))',
        borderRadius: 'var(--radius-xl)', padding: '32px 36px',
        color: 'white', marginBottom: 32, position: 'relative', overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', top: -30, right: -30, width: 160, height: 160,
          borderRadius: '50%', background: 'rgba(255,255,255,0.08)'
        }} />
        <div style={{
          position: 'absolute', bottom: -40, right: 60, width: 120, height: 120,
          borderRadius: '50%', background: 'rgba(255,255,255,0.05)'
        }} />
        <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: 6, position: 'relative' }}>
          {greeting()}, {user?.name?.split(' ')[0]}!
        </h2>
        <p style={{ fontSize: '0.9375rem', opacity: 0.85, position: 'relative', maxWidth: 500 }}>
          Selamat datang di SI-KOPENG. Akses informasi pengadaan barang dan jasa Kabupaten Ngada dengan mudah.
        </p>
      </div>

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, marginBottom: 32 }}>
        <StatCard
          icon="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          label="Total Peraturan"
          value={stats?.totalRegulations || 0}
          color="#f97316"
          to="/peraturan"
        />
        <StatCard
          icon="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          label="Konsultasi"
          value={stats?.totalConsultations || 0}
          color="#3b82f6"
          to="/konsultasi"
        />
        <StatCard
          icon="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342"
          label="Materi Pembelajaran"
          value={stats?.totalCourses || 0}
          color="#22c55e"
          to="/pembelajaran"
        />
        {(user?.role === 'admin' || user?.role === 'expert') && (
          <StatCard
            icon="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            label="Konsultasi Menunggu"
            value={stats?.pendingConsultations || 0}
            color="#eab308"
            to="/konsultasi"
          />
        )}
      </div>

      {/* Quick Actions */}
      <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>
        Akses Cepat
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        <Link to="/konsultasi" style={{ textDecoration: 'none' }}>
          <div className="card" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-md)', background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-primary)' }}>
              <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>Ajukan Konsultasi</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Tanyakan ahli pengadaan barang/jasa</div>
            </div>
          </div>
        </Link>
        <Link to="/peraturan" style={{ textDecoration: 'none' }}>
          <div className="card" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-md)', background: 'rgba(59,130,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6' }}>
              <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>Cari Peraturan</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Perpres, Permen LKPP, dan lainnya</div>
            </div>
          </div>
        </Link>
        <Link to="/pembelajaran" style={{ textDecoration: 'none' }}>
          <div className="card" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-md)', background: 'rgba(34,197,94,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#22c55e' }}>
              <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347" /></svg>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>Mulai Belajar</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Tingkatkan kompetensi pengadaan</div>
            </div>
          </div>
        </Link>
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16, border: '1px dashed var(--orange-300)', background: 'rgba(249,115,22,0.02)' }}>
          <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-md)', background: 'rgba(59,165,224,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#24A1DE' }}>
            <svg width="22" height="22" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.11.02-1.93 1.23-5.46 3.62-.51.35-.98.53-1.39.52-.46-.01-1.33-.26-1.98-.48-.8-.27-1.43-.42-1.37-.89.03-.25.38-.51 1.03-.78 4.04-1.76 6.74-2.92 8.09-3.48 3.85-1.6 4.64-1.88 5.17-1.89.11 0 .37.03.54.17.14.12.18.28.2.45-.02.07-.02.13-.03.19z"/></svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>Notifikasi Telegram</div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              {user?.telegram_chat_id ? 'Akun sudah terhubung' : 'Dapatkan notifikasi di HP'}
            </div>
          </div>
          {!user?.telegram_chat_id ? (
            <button 
              onClick={handleLinkTelegram}
              disabled={telegramLoading}
              className="btn btn-primary" 
              style={{ padding: '6px 12px', fontSize: '0.75rem' }}
            >
              {telegramLoading ? '...' : 'Hubungkan'}
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={handleReconnectTelegram}
                disabled={telegramLoading}
                className="btn btn-ghost btn-sm"
                style={{ fontSize: '0.75rem' }}
                title="Hubungkan ulang ke akun Telegram berbeda"
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" /></svg>
              </button>
              <button
                onClick={handleResetTelegram}
                className="btn btn-ghost btn-sm"
                style={{ fontSize: '0.75rem', color: 'var(--error)' }}
                title="Putuskan koneksi Telegram"
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
              <div style={{ color: 'var(--success-main)', display: 'flex', alignItems: 'center' }}>
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </div>
            </div>
          )}
          {!user?.telegram_chat_id && telegramToken && (
            <div style={{
              gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              background: 'rgba(59,165,224,0.08)', border: '1px dashed rgba(59,165,224,0.4)',
              borderRadius: 'var(--radius-md)', padding: '8px 12px', fontSize: '0.75rem',
              color: 'var(--text-secondary)'
            }}>
              <span>Jika link tidak berfungsi (payload dibuang Telegram), kirim token ini ke chat bot:</span>
              <code style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.75rem', fontWeight: 700,
                color: 'var(--text-primary)', userSelect: 'all'
              }}>{telegramToken}</code>
              <button
                onClick={copyTelegramToken}
                className="btn btn-ghost btn-sm"
                style={{ fontSize: '0.7rem', padding: '3px 8px' }}
              >
                Salin
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
